import { readDemoSnapshot } from "./demoSnapshot";
import { isKnownPmsLanguageId } from "../utils/pmsLanguages";

export const DEMO_BUSINESS_UNIT_ID = 900001;
export const DEMO_MODE_KEY = "pilcore_demo_mode_v1";
export const DEMO_STATE_KEY = "pilcore_demo_state_v1";
export const DEMO_TOKEN_KEY = "pilcore_demo_token_v1";
export const DEMO_USER_KEY = "pilcore_demo_user_v1";
export const DEMO_DEFAULT_LANGUAGE_ID = "es";
export const DEMO_READ_ONLY = true;

const DEMO_USER = {
  id: 900001,
  read_only: true,
  first_name: "Recepcion",
  last_name: "Hotel Travesia",
  name: "Recepcion Demo",
  email: "demo@hoteltravesia.test",
  role: "receptionist",
  active_business_unit_id: DEMO_BUSINESS_UNIT_ID,
  business_unit_ids: [DEMO_BUSINESS_UNIT_ID],
  business_unit_name: "Hotel Travesia",
  allowed_modules: ["pms"],
  preferences: {
    pms_theme_id: "light",
    pms_language_id: "es",
  },
  permissions: {
    "reservations.view_dashboard": true,
    "reservations.view_list": true,
    "reservations.view_payments": true,
    "reservations.view_availability": true,
    "reservations.view_detail": true,
    "reservations.create": false,
    "reservations.update": false,
    "reservations.delete": false,
    "reservations.delete_drafts": false,
    "reservations.add_payment": false,
    "reservations.manage_charges": false,
    "reservations.manage_extras": false,
    "reservations.override_rates": false,
    "stripe_transactions.manage": false,
    "front_desk.view_check_in": true,
    "front_desk.view_check_out": true,
    "rooms.view_matrix": true,
    "rooms.view_assignment": true,
    "rooms.view_blocks": true,
    "revenue.view_room_rates": true,
    "revenue.view_price_detail": true,
    "revenue.view_all_prices": true,
    "revenue.create_room_rate": false,
    "revenue.create_price": false,
    "revenue.delete_price": false,
    "revenue.manage_stop_sells": false,
    "revenue.apply_market_prices": false,
    "revenue.view_market_prices": false,
    "hotel_settings.view_customer_settings": true,
    "hotel_settings.view_stay_settings": true,
    "hotel_settings.view_reservation_flow": true,
    "hotel_settings.view_required_fields": true,
    "hotel_settings.view_required_roles": true,
    "hotel_settings.view_front_desk_settings": true,
    "hotel_settings.view_extras_tab": true,
    "hotel_settings.manage_extras_actions": false,
    "hotel_settings.view_reservation_charges_tab": true,
    "hotel_settings.manage_reservation_charges_actions": false,
    "hotel_settings.view_booking_engine_tab": true,
  },
};

function normalizeDemoLanguageId(languageId) {
  const normalizedLanguageId = String(languageId || "").trim().toLowerCase();
  return isKnownPmsLanguageId(normalizedLanguageId) ? normalizedLanguageId : "";
}

function createDemoUser(languageId) {
  const safeLanguageId = normalizeDemoLanguageId(languageId) || DEMO_DEFAULT_LANGUAGE_ID;

  return {
    ...DEMO_USER,
    preferences: {
      ...DEMO_USER.preferences,
      pms_language_id: safeLanguageId,
    },
  };
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && Boolean(window.sessionStorage);
}

function isDemoHostname() {
  if (typeof window === "undefined") return false;
  return window.location.hostname.toLowerCase() === "demo.pilcore.com";
}

export function isStaticDemoBuild() {
  return process.env.REACT_APP_PILCORE_DEMO_BUILD === "true";
}

export function isDemoMode() {
  if (typeof window === "undefined") return false;
  if (isStaticDemoBuild()) return true;
  if (isDemoHostname()) return true;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/demo") return true;
  try {
    return window.sessionStorage.getItem(DEMO_MODE_KEY) === "1";
  } catch (_error) {
    return false;
  }
}

export function markDemoDocument() {
  if (typeof document === "undefined") return;
  if (isDemoMode()) {
    document.documentElement.dataset.pilcoreDemo = "true";
    document.documentElement.dataset.pilcoreDemoReadOnly = String(DEMO_READ_ONLY);
  }
}

export function readDemoUser() {
  if (!canUseSessionStorage()) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(DEMO_USER_KEY) || "{}");
  } catch (_error) {
    return {};
  }
}

export function readDemoLanguageId() {
  if (typeof window === "undefined") return DEMO_DEFAULT_LANGUAGE_ID;

  const requestedLanguageId = normalizeDemoLanguageId(
    new URLSearchParams(window.location.search).get("lang")
  );
  if (requestedLanguageId) return requestedLanguageId;

  const storedLanguageId = normalizeDemoLanguageId(
    readDemoUser()?.preferences?.pms_language_id
  );
  return storedLanguageId || DEMO_DEFAULT_LANGUAGE_ID;
}

export function readDemoToken() {
  if (!canUseSessionStorage()) return "";
  return String(window.sessionStorage.getItem(DEMO_TOKEN_KEY) || "");
}

export function writeDemoToken(token) {
  const safeToken = String(token || "");
  if (canUseSessionStorage()) {
    if (safeToken) window.sessionStorage.setItem(DEMO_TOKEN_KEY, safeToken);
    else window.sessionStorage.removeItem(DEMO_TOKEN_KEY);
  }
  return safeToken;
}

export function writeDemoUser(user) {
  const safeUser = user && typeof user === "object" ? user : {};
  if (canUseSessionStorage()) {
    window.sessionStorage.setItem(DEMO_USER_KEY, JSON.stringify(safeUser));
  }
  return safeUser;
}

export function loadDemoState() {
  return readDemoSnapshot();
}

export function startDemoSession(languageId = readDemoLanguageId()) {
  const demoUser = createDemoUser(languageId);
  if (!canUseSessionStorage()) return demoUser;
  window.sessionStorage.setItem(DEMO_MODE_KEY, "1");
  window.sessionStorage.setItem(DEMO_TOKEN_KEY, "pilcore-demo-session");
  writeDemoUser(demoUser);
  // Retire the mutable v1 fixture cache; snapshot data stays in memory.
  window.sessionStorage.removeItem(DEMO_STATE_KEY);
  markDemoDocument();
  window.dispatchEvent(new CustomEvent("palantir-session-updated", { detail: demoUser }));
  return demoUser;
}
